/**
 * BATCH 5A — Vendor Registration & Documents.
 *
 * Opened from a vendor card in AccountingVendors.js. Three sections:
 *  - Registration details (GSTIN/PAN/bank/address/contact/agreement) —
 *    masked by default; Admin/Accounts can edit + reveal (audited);
 *    Agents see everything sensitive stripped, read-only.
 *  - Documents — upload allowed for any CRM-finance role; view/download/
 *    delete restricted to Admin/Accounts (signed URL, 5-min TTL).
 *  - Onboarding checklist + verification status (Admin/Accounts only can
 *    change status).
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Eye, EyeOff, Upload, FileText, Trash2, Download, CheckCircle2, Circle, CreditCard } from 'lucide-react';
import { getErrorMessage } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DOC_TYPES = [
  { value: 'gst_certificate', label: 'GST Certificate' },
  { value: 'pan_card', label: 'PAN Card' },
  { value: 'cancelled_cheque', label: 'Cancelled Cheque / Bank Proof' },
  { value: 'signed_agreement', label: 'Signed Agreement' },
];
const DOC_LABEL = Object.fromEntries(DOC_TYPES.map((d) => [d.value, d.label]));

const VERIFICATION_STATUSES = [
  { value: 'draft', label: 'Draft' },
  { value: 'pending_verification', label: 'Pending Verification' },
  { value: 'verified', label: 'Verified' },
  { value: 'needs_update', label: 'Needs Update' },
];
const STATUS_PILL = {
  draft: 'bg-slate-100 text-slate-600 border-slate-200',
  pending_verification: 'bg-amber-50 text-amber-700 border-amber-200',
  verified: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  needs_update: 'bg-rose-50 text-rose-700 border-rose-200',
};

const CHECKLIST_LABELS = {
  registrationDocsCollected: 'Registration docs collected',
  gstVerified: 'GST verified',
  bankDetailsConfirmed: 'Bank details confirmed',
  agreementSigned: 'Agreement signed',
  commercialTermsSet: 'Commercial terms set',
};

const EMPTY_REG = {
  gstNumber: '', panCard: '', bankAccountHolder: '', bankAccountNumber: '', bankIfsc: '', bankName: '',
  registeredAddress: '', primaryContactName: '', primaryContactPhone: '', primaryContactEmail: '', agreementRef: '',
};

// Vendor Payment Gateway Charges (2026-09) — must match backend
// VENDOR_PAYMENT_METHODS (routes/accounting.py).
const FEE_RATE_METHODS = [
  { value: 'personal_card', label: 'Personal Card' },
  { value: 'corporate_card', label: 'Corporate Card' },
  { value: 'neft', label: 'NEFT' },
  { value: 'imps', label: 'IMPS' },
];
const EMPTY_FEE_RATES = { personal_card: '0', corporate_card: '0', neft: '0', imps: '0' };

const VendorRegistrationDialog = ({ vendor, open, onClose, onRefresh, canEditSensitive, isAgent }) => {
  const [reg, setReg] = useState(EMPTY_REG);
  const [revealed, setRevealed] = useState(null);
  const [revealing, setRevealing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [docs, setDocs] = useState([]);
  const [checklist, setChecklist] = useState(null);
  const [docType, setDocType] = useState('gst_certificate');
  const [uploading, setUploading] = useState(false);
  const [statusForm, setStatusForm] = useState({ status: 'draft', notes: '' });
  const [savingStatus, setSavingStatus] = useState(false);
  const [feeRates, setFeeRates] = useState(EMPTY_FEE_RATES);
  const [savingFeeRates, setSavingFeeRates] = useState(false);

  const load = useCallback(async () => {
    if (!vendor) return;
    setReg({
      gstNumber: vendor.gstNumber || '', panCard: vendor.panCard || '',
      bankAccountHolder: vendor.bankAccountHolder || '', bankAccountNumber: vendor.bankAccountNumber || '',
      bankIfsc: vendor.bankIfsc || '', bankName: vendor.bankName || '',
      registeredAddress: vendor.registeredAddress || '', primaryContactName: vendor.primaryContactName || '',
      primaryContactPhone: vendor.primaryContactPhone || '', primaryContactEmail: vendor.primaryContactEmail || '',
      agreementRef: vendor.agreementRef || '',
    });
    setStatusForm({ status: vendor.verificationStatus || 'draft', notes: vendor.verificationNotes || '' });
    const rates = vendor.paymentMethodFeeRates || {};
    setFeeRates({
      personal_card: String(rates.personal_card || 0),
      corporate_card: String(rates.corporate_card || 0),
      neft: String(rates.neft || 0),
      imps: String(rates.imps || 0),
    });
    setRevealed(null);
    try {
      const [docsRes, checklistRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/accounting/vendors/${vendor.vendorId}/documents`),
        axios.get(`${BACKEND_URL}/api/accounting/vendors/${vendor.vendorId}/onboarding-checklist`),
      ]);
      setDocs(docsRes.data || []);
      setChecklist(checklistRes.data);
    } catch {
      /* non-fatal — dialog still usable for editing */
    }
  }, [vendor]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const set = (key, val) => setReg((prev) => ({ ...prev, [key]: val }));

  const handleSaveReg = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/accounting/vendors/${vendor.vendorId}/registration-details`, reg);
      toast.success('Registration details saved');
      setRevealed(null);
      onRefresh();
      load();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to save registration details'));
    } finally { setSaving(false); }
  };

  const handleReveal = async () => {
    setRevealing(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/accounting/vendors/${vendor.vendorId}/reveal-pii`, {});
      setRevealed(res.data.revealed);
      toast.info('Reveal is audited — logged with your account and timestamp');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to reveal'));
    } finally { setRevealing(false); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('docType', docType);
      await axios.post(`${BACKEND_URL}/api/accounting/vendors/${vendor.vendorId}/documents`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Document uploaded');
      load();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Upload failed'));
    } finally { setUploading(false); e.target.value = ''; }
  };

  const handleViewDoc = async (doc) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/accounting/vendors/${vendor.vendorId}/documents/${doc.vendorDocId}/sign`);
      window.open(`${BACKEND_URL}${res.data.url}`, '_blank', 'noopener');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to open document'));
    }
  };

  const handleDeleteDoc = async (doc) => {
    if (!window.confirm(`Delete ${DOC_LABEL[doc.docType] || doc.docType}?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/accounting/vendors/${vendor.vendorId}/documents/${doc.vendorDocId}`);
      toast.success('Document deleted');
      load();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to delete document'));
    }
  };

  const handleSaveStatus = async (e) => {
    e.preventDefault();
    setSavingStatus(true);
    try {
      await axios.put(`${BACKEND_URL}/api/accounting/vendors/${vendor.vendorId}/verification-status`, statusForm);
      toast.success('Verification status updated');
      onRefresh();
      load();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to update status'));
    } finally { setSavingStatus(false); }
  };

  const handleSaveFeeRates = async (e) => {
    e.preventDefault();
    setSavingFeeRates(true);
    try {
      const payload = Object.fromEntries(FEE_RATE_METHODS.map((m) => [m.value, parseFloat(feeRates[m.value]) || 0]));
      await axios.put(`${BACKEND_URL}/api/accounting/vendors/${vendor.vendorId}/payment-method-fee-rates`, payload);
      toast.success('Payment method fee rates saved');
      onRefresh();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to save fee rates'));
    } finally { setSavingFeeRates(false); }
  };

  if (!vendor) return null;
  const val = (key) => (revealed ? revealed[key] : reg[key]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="vendor-registration-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Vendor Registration — {vendor.name}
            <span
              className={`inline-flex items-center text-[10px] font-semibold border rounded-full px-2 py-0.5 ${STATUS_PILL[vendor.verificationStatus] || STATUS_PILL.draft}`}
              data-testid="vendor-verification-badge"
            >
              {(VERIFICATION_STATUSES.find((s) => s.value === (vendor.verificationStatus || 'draft')) || {}).label}
            </span>
          </DialogTitle>
        </DialogHeader>

        {/* ---- Onboarding checklist ---- */}
        {checklist && (
          <div className="border border-slate-200 rounded-lg p-3 bg-slate-50" data-testid="vendor-onboarding-checklist">
            <p className="text-xs font-semibold text-slate-600 mb-2">Onboarding readiness — {checklist.completed}/{checklist.total}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {Object.entries(CHECKLIST_LABELS).map(([key, label]) => {
                const ok = !!checklist.checklist?.[key];
                return (
                  <div key={key} className="flex items-center gap-1.5 text-xs" data-testid={`checklist-item-${key}`}>
                    {ok ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <Circle className="h-3.5 w-3.5 text-slate-300" />}
                    <span className={ok ? 'text-slate-700' : 'text-slate-400'}>{label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ---- Registration details ---- */}
        <form onSubmit={handleSaveReg} className="space-y-3" data-testid="vendor-registration-form">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-700">Registration & bank details</p>
            {canEditSensitive && (
              <Button type="button" size="sm" variant="outline" onClick={handleReveal} disabled={revealing} data-testid="reveal-vendor-pii-btn">
                {revealed ? <><EyeOff className="h-3.5 w-3.5 mr-1.5" />Hide</> : <><Eye className="h-3.5 w-3.5 mr-1.5" />Reveal (audited)</>}
              </Button>
            )}
          </div>
          {isAgent && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5" data-testid="agent-pii-restricted-note">
              GSTIN, PAN and bank details are restricted to Admin/Accounts.
            </p>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>GSTIN</Label>
              <Input value={revealed ? val('gstNumber') : (isAgent ? '' : reg.gstNumber)} disabled={!canEditSensitive || !!revealed}
                onChange={(e) => set('gstNumber', e.target.value)} className="mt-1" data-testid="reg-gst-input"
                placeholder={isAgent ? 'Restricted' : ''} />
            </div>
            <div>
              <Label>PAN</Label>
              <Input value={revealed ? val('panCard') : (isAgent ? '' : reg.panCard)} disabled={!canEditSensitive || !!revealed}
                onChange={(e) => set('panCard', e.target.value)} className="mt-1" data-testid="reg-pan-input"
                placeholder={isAgent ? 'Restricted' : ''} />
            </div>
            <div>
              <Label>Bank Account Holder</Label>
              <Input value={revealed ? val('bankAccountHolder') : (isAgent ? '' : reg.bankAccountHolder)} disabled={!canEditSensitive || !!revealed}
                onChange={(e) => set('bankAccountHolder', e.target.value)} className="mt-1" data-testid="reg-bank-holder-input"
                placeholder={isAgent ? 'Restricted' : ''} />
            </div>
            <div>
              <Label>Bank Account Number</Label>
              <Input value={revealed ? val('bankAccountNumber') : (isAgent ? '' : reg.bankAccountNumber)} disabled={!canEditSensitive || !!revealed}
                onChange={(e) => set('bankAccountNumber', e.target.value)} className="mt-1" data-testid="reg-bank-account-input"
                placeholder={isAgent ? 'Restricted' : ''} />
            </div>
            <div>
              <Label>IFSC</Label>
              <Input value={revealed ? val('bankIfsc') : (isAgent ? '' : reg.bankIfsc)} disabled={!canEditSensitive || !!revealed}
                onChange={(e) => set('bankIfsc', e.target.value)} className="mt-1" data-testid="reg-bank-ifsc-input"
                placeholder={isAgent ? 'Restricted' : ''} />
            </div>
            <div>
              <Label>Bank Name</Label>
              <Input value={revealed ? val('bankName') : (isAgent ? '' : reg.bankName)} disabled={!canEditSensitive || !!revealed}
                onChange={(e) => set('bankName', e.target.value)} className="mt-1" data-testid="reg-bank-name-input"
                placeholder={isAgent ? 'Restricted' : ''} />
            </div>
            <div className="col-span-2">
              <Label>Registered Address</Label>
              <Textarea value={reg.registeredAddress} disabled={!canEditSensitive}
                onChange={(e) => set('registeredAddress', e.target.value)} className="mt-1" rows={2} data-testid="reg-address-input" />
            </div>
            <div>
              <Label>Primary Contact Name</Label>
              <Input value={reg.primaryContactName} disabled={!canEditSensitive}
                onChange={(e) => set('primaryContactName', e.target.value)} className="mt-1" data-testid="reg-contact-name-input" />
            </div>
            <div>
              <Label>Primary Contact Phone</Label>
              <Input value={reg.primaryContactPhone} disabled={!canEditSensitive}
                onChange={(e) => set('primaryContactPhone', e.target.value)} className="mt-1" data-testid="reg-contact-phone-input" />
            </div>
            <div>
              <Label>Primary Contact Email</Label>
              <Input value={reg.primaryContactEmail} disabled={!canEditSensitive}
                onChange={(e) => set('primaryContactEmail', e.target.value)} className="mt-1" data-testid="reg-contact-email-input" />
            </div>
            <div>
              <Label>Agreement Reference</Label>
              <Input value={reg.agreementRef} disabled={!canEditSensitive}
                onChange={(e) => set('agreementRef', e.target.value)} className="mt-1" data-testid="reg-agreement-ref-input" />
            </div>
          </div>
          {canEditSensitive && (
            <div className="flex justify-end">
              <Button type="submit" disabled={saving || !!revealed} data-testid="save-registration-btn" className="bg-teal-700 hover:bg-teal-800 text-white">
                {saving ? 'Saving…' : 'Save Registration Details'}
              </Button>
            </div>
          )}
        </form>

        {/* ---- Payment Method Fee Rates (2026-09) ---- */}
        {canEditSensitive && (
          <form onSubmit={handleSaveFeeRates} className="border-t border-slate-100 pt-3 space-y-3" data-testid="vendor-fee-rates-form">
            <p className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
              <CreditCard className="h-4 w-4" /> Payment Method Fee Rates
            </p>
            <p className="text-xs text-slate-400">
              Fee % this vendor charges when we pay them via each rail (e.g. TBO: 1.65% personal card, 2.42% corporate card). Auto-applied on the Expense form — never trusted from the payer, always looked up from here.
            </p>
            <div className="grid grid-cols-2 gap-3">
              {FEE_RATE_METHODS.map((m) => (
                <div key={m.value}>
                  <Label>{m.label} (%)</Label>
                  <Input
                    type="number" step="0.01" min="0"
                    value={feeRates[m.value]}
                    onChange={(e) => setFeeRates((prev) => ({ ...prev, [m.value]: e.target.value }))}
                    className="mt-1" data-testid={`fee-rate-${m.value}-input`}
                  />
                </div>
              ))}
            </div>
            <div className="flex justify-end">
              <Button type="submit" disabled={savingFeeRates} data-testid="save-fee-rates-btn" className="bg-teal-700 hover:bg-teal-800 text-white">
                {savingFeeRates ? 'Saving…' : 'Save Fee Rates'}
              </Button>
            </div>
          </form>
        )}

        {/* ---- Documents ---- */}
        <div className="border-t border-slate-100 pt-3 space-y-2" data-testid="vendor-documents-section">
          <p className="text-sm font-semibold text-slate-700">Documents</p>
          <div className="flex items-center gap-2">
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger className="h-9 w-56" data-testid="doc-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DOC_TYPES.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <label className="inline-flex">
              <input type="file" className="hidden" onChange={handleUpload} data-testid="doc-file-input" accept=".pdf,.png,.jpg,.jpeg,.webp" />
              <Button type="button" size="sm" variant="outline" disabled={uploading} data-testid="upload-doc-btn" asChild>
                <span><Upload className="h-3.5 w-3.5 mr-1.5" />{uploading ? 'Uploading…' : 'Upload'}</span>
              </Button>
            </label>
          </div>
          <div className="space-y-1">
            {docs.length === 0 ? (
              <p className="text-xs text-slate-400 italic">No documents uploaded yet.</p>
            ) : docs.map((d) => (
              <div key={d.vendorDocId} className="flex items-center justify-between text-xs border border-slate-100 rounded px-2 py-1.5" data-testid={`doc-row-${d.vendorDocId}`}>
                <span className="flex items-center gap-1.5 text-slate-700">
                  <FileText className="h-3.5 w-3.5 text-slate-400" />
                  {DOC_LABEL[d.docType] || d.docType}
                  <span className="text-slate-400">· {d.fileName}</span>
                </span>
                {canEditSensitive ? (
                  <span className="flex items-center gap-1">
                    <Button type="button" size="sm" variant="ghost" onClick={() => handleViewDoc(d)} data-testid={`doc-view-${d.vendorDocId}`}>
                      <Download className="h-3.5 w-3.5 text-slate-500" />
                    </Button>
                    <Button type="button" size="sm" variant="ghost" onClick={() => handleDeleteDoc(d)} data-testid={`doc-delete-${d.vendorDocId}`}>
                      <Trash2 className="h-3.5 w-3.5 text-red-400" />
                    </Button>
                  </span>
                ) : (
                  <span className="text-slate-300 text-[10px]">Admin/Accounts only</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ---- Verification status ---- */}
        <form onSubmit={handleSaveStatus} className="border-t border-slate-100 pt-3 space-y-2" data-testid="vendor-verification-form">
          <p className="text-sm font-semibold text-slate-700">Verification status</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Status</Label>
              <Select value={statusForm.status} onValueChange={(v) => setStatusForm((p) => ({ ...p, status: v }))} disabled={!canEditSensitive}>
                <SelectTrigger className="mt-1" data-testid="verification-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {VERIFICATION_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes</Label>
              <Input value={statusForm.notes} disabled={!canEditSensitive}
                onChange={(e) => setStatusForm((p) => ({ ...p, notes: e.target.value }))} className="mt-1" data-testid="verification-notes-input" />
            </div>
          </div>
          {!canEditSensitive && <p className="text-xs text-slate-500">Only Admin/Accounts can change verification status.</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose} data-testid="close-registration-dialog-btn">Close</Button>
            {canEditSensitive && (
              <Button type="submit" disabled={savingStatus} data-testid="save-verification-btn" className="bg-teal-700 hover:bg-teal-800 text-white">
                {savingStatus ? 'Saving…' : 'Save Status'}
              </Button>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default VendorRegistrationDialog;
